import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Youtube, Facebook, Linkedin, Instagram, ArrowUpRight } from "lucide-react";
import { FIRM, NAV } from "../data/content";

const SOCIALS = [
  { label: "YouTube", href: "https://www.youtube.com/@canrk", Icon: Youtube },
  { label: "Facebook", href: "https://www.facebook.com/people/CA-Nitta-Ravi-Kishore/1576287120/", Icon: Facebook },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/ca-ravi-kishore-nitta-06152655/", Icon: Linkedin },
  { label: "Instagram", href: "https://www.instagram.com/nittakishore/", Icon: Instagram },
  // WhatsApp icon isn't in lucide-react — use an inline SVG glyph
  { label: "WhatsApp", href: "https://www.whatsapp.com/channel/0029VbAPktx0lwgiEuk7HC0C", Icon: null },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#0A192F] text-white relative overflow-hidden">
      <div className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-[#C5A059]/8 blur-[100px]" aria-hidden />

      <div className="relative container-x py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <p className="eyebrow text-[#C5A059]">Chartered Accountancy Firm</p>
            <h3 className="editorial-display text-[clamp(2.5rem,4.5vw,4.5rem)] mt-8 max-w-[20ch]">
              Tax, audit and advisory <span className="italic text-[#C5A059]">since 1998.</span>
            </h3>
            <p className="text-white/65 mt-8 max-w-md leading-[1.85]">
              A Chartered Accountancy firm offering services in taxation, audit, compliance and advisory to businesses, professionals and individuals.
            </p>
            <div data-testid="footer-socials" className="mt-10 flex items-center gap-3 flex-wrap">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 text-white/70 hover:text-[#0A192F] hover:bg-[#C5A059] hover:border-[#C5A059] transition-all duration-300"
                >
                  {Icon ? (
                    <Icon size={16} strokeWidth={1.6} />
                  ) : (
                    // WhatsApp glyph
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.49 0 .15 5.34.15 11.91c0 2.1.55 4.15 1.6 5.96L0 24l6.27-1.65a11.85 11.85 0 0 0 5.77 1.47h.01c6.55 0 11.9-5.34 11.9-11.91 0-3.18-1.24-6.17-3.43-8.43Zm-8.48 18.32h-.01a9.84 9.84 0 0 1-5.02-1.38l-.36-.21-3.72.98 1-3.63-.23-.37a9.85 9.85 0 1 1 18.27-5.28c0 5.43-4.42 9.89-9.93 9.89Zm5.43-7.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.78-1.67-2.08-.17-.3-.02-.46.13-.61.13-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.21 5.08 4.5.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.34Z"/>
                    </svg>
                  )}
                </a>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-4 text-white/55 text-sm flex-wrap">
              <span>FCA</span>
              <span className="h-1 w-1 rounded-full bg-[#C5A059]" />
              <span>ACMA</span>
              <span className="h-1 w-1 rounded-full bg-[#C5A059]" />
              <span>DISA (ICAI)</span>
              <span className="h-1 w-1 rounded-full bg-[#C5A059]" />
              <span>Member, ICAI</span>
              <span className="h-1 w-1 rounded-full bg-[#C5A059]" />
              <span>Member, CMA</span>
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-[#C5A059] text-[10px]">Navigate</p>
            <ul className="mt-6 space-y-4">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s|&/g, "-")}`} className="text-white/75 hover:text-[#C5A059] transition-colors text-[0.95rem] inline-flex items-center gap-2 group">
                    {n.label}
                    <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-[#C5A059] text-[10px]">Office</p>
            <ul className="mt-6 space-y-5 text-white/80 text-[0.95rem]">
              <li className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 text-[#C5A059] flex-shrink-0" /> <span>{FIRM.contact.address1}<br /><span className="text-white/55">{FIRM.contact.address2}</span></span></li>
              <li className="flex items-center gap-3"><Phone size={16} className="text-[#C5A059]" /> <a href={`tel:${FIRM.contact.phone}`} className="hover:text-[#C5A059]">{FIRM.contact.phone}</a></li>
              <li className="flex items-center gap-3"><Mail size={16} className="text-[#C5A059]" /> <a href={`mailto:${FIRM.contact.email}`} className="hover:text-[#C5A059]">{FIRM.contact.email}</a></li>
              <li className="flex items-center gap-3"><Youtube size={16} className="text-[#C5A059]" /> <a href={FIRM.contact.youtube} target="_blank" rel="noreferrer" className="hover:text-[#C5A059]">CA. NRK</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-3 justify-between text-white/45 text-sm">
          <p>© {new Date().getFullYear()} CA. Nitta Ravi Kishore · All rights reserved.</p>
          <p className="overline text-[10px]">Tax · Audit · Advisory</p>
        </div>
      </div>
    </footer>
  );
}
